import { moodLabel, signedFigure } from '@adhd/shared';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, type Person, type PersonDay } from '../lib/api.js';
import { bandOf, inkOn } from '../lib/band.js';

/**
 * One person: the days they were in, and where each of those days ended.
 * Release 2 of 2.0 (docs/PRD-2.0.md §4).
 *
 * Points, never a verdict. Each day is a cell in its condition colour with
 * the day-end figure, and the entry that named them beside it; there is no
 * average of "days with EJ", because an average here would be a sentence
 * about a relationship that nobody wrote. Days with no check-in are drawn as
 * blank cells, not left out: the day was still a day with them in it.
 */
export function PersonPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<(Person & { daysList: PersonDay[] }) | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!id) return;
    void api.person(id).then(setData).catch(() => setFailed(true));
  }, [id]);

  if (failed) {
    return (
      <div className="stack">
        <Link className="linkish" to="/people">
          ‹ People
        </Link>
        <div className="banner banner-error">That did not load. They may have been merged or removed.</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '40dvh' }}>
        <span className="spinner" />
      </div>
    );
  }

  const byHand = data.daysList.filter((d) => d.source === 'you').length;

  return (
    <div className="stack-lg stack">
      <Link className="linkish" to="/people">
        ‹ People
      </Link>
      <header className="page-header">
        <h1>{data.name}</h1>
        <p>
          In {data.days} {data.days === 1 ? 'day' : 'days'} of your record
          {data.aliases.length > 0 ? ` · also ${data.aliases.join(', ')}` : ''}
          {byHand > 0 ? ` · ${byHand} added by you` : ''}
        </p>
      </header>

      {data.daysList.length === 0 ? (
        <p className="muted">No days yet.</p>
      ) : (
        <ol className="stack-sm stack person-days" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {data.daysList.map((d) => {
            const band = d.lastMood === null ? null : bandOf(d.lastMood);
            return (
              <li key={d.day} className="person-day">
                <span
                  className={`ten-b person-cell${band ? ` ten-${band} ten-ink-${inkOn(band)}` : ' ten-blank'}`}
                  title={d.lastMood === null ? 'no check-in' : `${moodLabel(d.lastMood)} ${signedFigure(d.lastMood)}`}
                >
                  {d.lastMood === null ? '·' : signedFigure(d.lastMood)}
                </span>
                <span className="person-day-when">{longDate(d.day)}</span>
                {d.voiceLogId ? (
                  <Link className="person-day-entry" to={`/journal/${d.voiceLogId}`}>
                    {d.title ?? 'The entry that named them'}
                  </Link>
                ) : (
                  <span className="faint">{d.source === 'you' ? 'added by you' : ''}</span>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <p className="faint">
        Where each day ended, in its colour, with the entry that named them. No average: a figure for
        "days with {data.name}" would be a sentence about a person that nobody wrote.
      </p>
    </div>
  );
}

function longDate(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}
